import { Shield, Target, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About Us"
        description="Learn about MarketsReview.org, operated by VIP Affiliate Network Ltd. An independent comparison platform providing objective Forex & CFD broker reviews and educational resources."
        canonical="/about"
      />
      <section className="bg-slate-900 text-white py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight font-['Outfit'] mb-4">About MarketsReview</h1>
          <p className="text-base text-slate-300 max-w-2xl">
            An independent comparison platform operated by VIP Affiliate Network Ltd, providing objective Forex & CFD broker reviews and educational resources.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-10">
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 font-['Outfit'] mb-4">Who We Are</h2>
            <p className="text-base text-slate-700 leading-relaxed mb-4">
              MarketsReview.org is operated by VIP Affiliate Network Ltd, a RoboForex partner. VIP Affiliate Network Ltd is a digital media company focused on
              providing transparent, educational, and objective financial content. Our platform was created to offer
              traders an independent resource for comparing forex and CFD brokers without bias or promotional influence.
            </p>
            <p className="text-base text-slate-700 leading-relaxed">
              We believe that transparent, unbiased information is essential for traders to make informed decisions
              about which broker best suits their individual needs and trading style. Our content is produced independently
              and does not represent the views of any listed broker.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Shield,
                title: 'Independence',
                desc: 'Our reviews and assessments are based on objective criteria. We present all brokers with equal visual weight and do not favour any specific broker. We avoid language that implies superiority or recommendation.'
              },
              {
                icon: Target,
                title: 'Accuracy',
                desc: 'We strive to maintain accurate, up-to-date information about broker trading conditions, fees, regulation, and platforms. Information is sourced from official broker websites and regulatory registers.'
              },
              {
                icon: Users,
                title: 'Education',
                desc: 'Beyond broker comparisons, we provide educational guides on trading risk, money management, technical analysis, and financial planning to help traders at all experience levels.'
              }
            ].map(item => (
              <div key={item.title} className="bg-white border border-slate-200 rounded-xl p-6">
                <item.icon className="w-8 h-8 text-blue-600 mb-4" />
                <h3 className="text-base font-semibold text-slate-900 font-['Outfit'] mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 font-['Outfit'] mb-4">Our Review Methodology</h2>
            <p className="text-base text-slate-700 leading-relaxed mb-4">
              Each broker assessment on MarketsReview.org is based on a structured evaluation of the following criteria:
            </p>
            <ul className="space-y-2 text-sm text-slate-700">
              <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 shrink-0" /> <strong className="text-slate-900">Regulation & Safety:</strong> Verification of regulatory licenses, client fund protection measures, and tier classification of regulators.</li>
              <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 shrink-0" /> <strong className="text-slate-900">Trading Costs:</strong> Analysis of spreads, commissions, swap rates, inactivity fees, and other costs.</li>
              <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 shrink-0" /> <strong className="text-slate-900">Platforms & Tools:</strong> Evaluation of trading platforms, charting tools, execution quality, and mobile trading capabilities.</li>
              <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 shrink-0" /> <strong className="text-slate-900">Instrument Range:</strong> Diversity of tradable markets and asset classes available.</li>
              <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 shrink-0" /> <strong className="text-slate-900">Customer Service:</strong> Responsiveness, availability, language support, and quality of assistance.</li>
            </ul>
            <p className="text-sm text-slate-600 mt-4">
              All brokers are assessed using the same criteria and presented with equal visual weight. We do not use terms
              such as "best broker," "top pick," or "recommended" as these imply advice or superiority.
            </p>
          </section>

          <section className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-red-800 font-['Outfit'] mb-3">Important Notice</h2>
            <p className="text-sm text-red-700 leading-relaxed">
              MarketsReview.org does not provide financial advice, investment advice, or trading recommendations.
              CFDs are complex instruments and come with a high risk of losing money rapidly due to leverage.
              Between 70-80% of retail investor accounts lose money when trading CFDs. You should consider whether you
              understand how CFDs work and whether you can afford to take the high risk of losing your money.
              Always consult a qualified financial advisor before making trading decisions.
            </p>
          </section>

          <section className="bg-slate-50 border border-slate-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-slate-900 font-['Outfit'] mb-3">Affiliate Disclosure</h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">
              MarketsReview.org is operated by VIP Affiliate Network Ltd, a RoboForex partner, and may receive compensation from brokers
              listed on this site when users click on affiliate links and register accounts. This compensation helps fund
              the operation of this Website. However, it does not influence our editorial content, assessments, or the
              order in which brokers appear.
            </p>
            <p className="text-sm text-slate-600 leading-relaxed">
              We are not affiliated with, endorsed by, or officially connected to any broker mentioned on this site.
              All broker names, logos, and trademarks are the property of their respective owners and are used solely
              for identification purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 font-['Outfit'] mb-4">Contact</h2>
            <p className="text-base text-slate-700 leading-relaxed">
              <strong>VIP Affiliate Network Ltd</strong><br />
              Email: <a href="mailto:info@vipaffiliatenetwork.com" className="text-blue-600 hover:text-blue-700">info@vipaffiliatenetwork.com</a><br />
              Website: <a href="https://marketsreview.org" className="text-blue-600 hover:text-blue-700">marketsreview.org</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
